import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { signIn, signOut as cognitoSignOut, restoreSession, type AuthUser } from "../lib/auth";

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthCtx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    restoreSession().then((s) => {
      if (s) { setUser(s.user); setToken(s.token); }
      setLoading(false);
    });
  }, []);

  const login = async (email: string, password: string) => {
    const s = await signIn(email, password);
    setUser(s.user); setToken(s.token);
  };

  const logout = () => { cognitoSignOut(); setUser(null); setToken(null); };

  return <AuthCtx.Provider value={{ user, token, loading, login, logout }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
